// =============================================================================
// Onboarding Page - Benvenuto per utenti senza organizzazione:
// accetta un invito pendente oppure crea la tua prima organizzazione
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Mail, Check, X } from 'lucide-react';
import { apiClient } from '../api/client';
import { useTenantStore } from '../store/tenantStore';
import { useAuthStore } from '../store/authStore';
import { generateSlug } from '../lib/utils';
import { DeletedOrganizations } from '../components/common/DeletedOrganizations';
import { AccountFooter } from '../components/common/AccountFooter';
import {
  useMyInvites,
  useAcceptMyInvite,
  useDeclineMyInvite,
  refreshMyMemberships,
  MyInvite,
} from '../api/hooks';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { selectTenant, setMemberships } = useTenantStore();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inviti pendenti destinati alla mia email
  const { data: invites = [], isLoading: invitesLoading } = useMyInvites(isAuthenticated);
  const acceptInvite = useAcceptMyInvite();
  const declineInvite = useDeclineMyInvite();
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);

  // Redirect se non autenticato
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleNameChange = (value: string) => {
    setName(value);
    setSlug(generateSlug(value));
  };

  // Dopo aver ottenuto una membership (invito accettato) entra direttamente
  const enterOrganization = async (organizationId: string) => {
    const memberships = await refreshMyMemberships();
    const accepted = memberships.find((m) => m.organizationId === organizationId);
    if (accepted) {
      selectTenant(accepted);
      navigate('/scheduler', { replace: true });
    } else if (memberships.length > 0) {
      navigate('/select-tenant', { replace: true });
    }
  };

  const handleAcceptInvite = async (invite: MyInvite) => {
    setProcessingInviteId(invite.id);
    setError(null);
    try {
      await acceptInvite.mutateAsync(invite.id);
      await enterOrganization(invite.organizationId);
    } catch (err: any) {
      // L'invito potrebbe non essere più valido: la lista si ricarica da sola (onSettled)
      const msg = err.response?.data?.message || t('onboarding.inviteError');
      setError(typeof msg === 'string' ? msg : msg[0]);
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleDeclineInvite = async (invite: MyInvite) => {
    setProcessingInviteId(invite.id);
    setError(null);
    try {
      await declineInvite.mutateAsync(invite.id);
    } catch (err: any) {
      const msg = err.response?.data?.message || t('onboarding.inviteError');
      setError(typeof msg === 'string' ? msg : msg[0]);
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleLogout = () => {
    useTenantStore.getState().clearTenant();
    logout();
    navigate('/login', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/tenants', { name, slug });
      const tenant = response.data;

      const membership = {
        organizationId: tenant.id,
        tenantName: tenant.name,
        tenantSlug: tenant.slug,
        timezone: tenant.timezone,
        role: 'OWNER' as const,
      };

      setMemberships([membership]);
      selectTenant(membership);
      navigate('/scheduler', { replace: true });
    } catch (err: any) {
      const msg =
        err.response?.data?.message || 'Errore durante la creazione';
      setError(typeof msg === 'string' ? msg : msg[0]);
    } finally {
      setIsLoading(false);
    }
  };

  const roleLabel: Record<string, string> = {
    OWNER: tCommon('roles.OWNER'),
    ADMIN: tCommon('roles.ADMIN'),
    WORKER: tCommon('roles.WORKER'),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-200">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('onboarding.greeting', { name: user?.firstName })}
          </h1>
          <p className="text-gray-500 mt-1">
            {invites.length > 0
              ? t('onboarding.subtitleWithInvites')
              : t('onboarding.subtitle')}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Inviti pendenti */}
        {invitesLoading ? (
          <div className="flex justify-center py-4 mb-4">
            <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
          </div>
        ) : (
          invites.length > 0 && (
            <div className="space-y-3 mb-6">
              <h2 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-600" />
                {t('onboarding.pendingInvites')}
              </h2>
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="bg-white rounded-xl p-4 shadow-sm border border-emerald-200 flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {invite.tenantName}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {roleLabel[invite.role] || invite.role}
                      {invite.invitedByName &&
                        ` · ${t('onboarding.invitedBy', { name: invite.invitedByName })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleDeclineInvite(invite)}
                      disabled={processingInviteId !== null}
                      title={t('onboarding.declineInvite')}
                      className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 disabled:opacity-50 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAcceptInvite(invite)}
                      disabled={processingInviteId !== null}
                      className="btn-primary px-3 py-2 text-sm flex items-center gap-1.5"
                    >
                      {processingInviteId === invite.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      {t('onboarding.acceptInvite')}
                    </button>
                  </div>
                </div>
              ))}

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-emerald-50/0 text-gray-500 backdrop-blur-sm">
                    {t('onboarding.orCreate')}
                  </span>
                </div>
              </div>
            </div>
          )
        )}

        {/* Eliminate di recente: chi elimina la sua unica organizzazione
            finisce qui, e deve poterla riprendere */}
        <DeletedOrganizations enabled={isAuthenticated} />

        {/* Form crea organizzazione */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('onboarding.orgName')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t('onboarding.orgNamePlaceholder')}
              className="w-full px-3 py-2 input outline-none"
              required
              maxLength={255}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('onboarding.slug')}
            </label>
            <div className="flex items-center">
              <span className="text-sm text-gray-400 mr-1">{t('onboarding.slugPrefix')}</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder={t('onboarding.slugPlaceholder')}
                pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                className="flex-1 px-3 py-2 input outline-none"
                required
                maxLength={100}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {t('onboarding.slugHint')}
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading || !name.trim() || !slug.trim()}
            className="btn-primary w-full py-2.5 px-4 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('onboarding.creating')}
              </>
            ) : (
              t('onboarding.createButton')
            )}
          </button>
        </form>

        {/* Account: profilo, lingua e uscita */}
        <AccountFooter onLogout={handleLogout} />
      </div>
    </div>
  );
}
